import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useExtraCostTypes,
  useCreateExtraCostType,
  useUpdateExtraCostType,
  useDeleteExtraCostType,
} from '../../hooks/useEmployeeExtraCostTypes';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';

export default function EmployeeExtraCostTypePage() {
  const navigate = useNavigate();
  const { data: types, isLoading } = useExtraCostTypes();
  const createMutation = useCreateExtraCostType();
  const updateMutation = useUpdateExtraCostType();
  const deleteMutation = useDeleteExtraCostType();

  const [showForm, setShowForm] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [recurring, setRecurring] = useState(false);
  const [frequencyMonths, setFrequencyMonths] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingRecurring, setEditingRecurring] = useState(false);
  const [editingFrequency, setEditingFrequency] = useState('');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    try {
      await createMutation.mutateAsync({
        name,
        recurring,
        frequencyMonths: recurring && frequencyMonths ? parseInt(frequencyMonths, 10) : null,
      });
      setShowForm(false);
      setName('');
      setRecurring(false);
      setFrequencyMonths('');
    } catch (err) {
      setFormError((err as Error).message);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId === null) return;
    try {
      await updateMutation.mutateAsync({
        id: editingId,
        name: editingName,
        recurring: editingRecurring,
        frequencyMonths: editingRecurring && editingFrequency ? parseInt(editingFrequency, 10) : null,
      });
      setEditingId(null);
      setEditingName('');
      setEditingRecurring(false);
      setEditingFrequency('');
    } catch (err) {
      setFormError((err as Error).message);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(deleteId);
      setDeleteId(null);
    } catch (err) {
      setDeleteError((err as Error).message);
    }
  };

  const startEditing = (id: number, currentName: string, isRecurring: boolean, freq: number | null) => {
    setEditingId(id);
    setEditingName(currentName);
    setEditingRecurring(isRecurring);
    setEditingFrequency(freq ? String(freq) : '');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName('');
    setEditingRecurring(false);
    setEditingFrequency('');
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Spinner /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <button onClick={() => navigate('/admin')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
          &larr; Back to Admin
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Employee Extra Cost Types</h1>
          <Button size="sm" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : 'Add Type'}
          </Button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          {formError && <p className="text-sm text-red-600">{formError}</p>}
          <div className="flex gap-3 items-center">
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Type name *"
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <label className="flex items-center gap-1.5 text-sm text-gray-700 whitespace-nowrap">
              <input
                type="checkbox"
                checked={recurring}
                onChange={(e) => setRecurring(e.target.checked)}
                className="rounded border-gray-300"
              />
              Recurring
            </label>
            {recurring && (
              <input
                type="number"
                min="1"
                value={frequencyMonths}
                onChange={(e) => setFrequencyMonths(e.target.value)}
                placeholder="Months"
                className="w-20 rounded-lg border border-gray-300 px-2 py-2 text-sm"
              />
            )}
            <Button type="submit" size="sm" isLoading={createMutation.isPending}>Create</Button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recurring</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {types?.map((type) => (
              <tr key={type.id} className="hover:bg-gray-50">
                {editingId === type.id ? (
                  <>
                    <td className="px-6 py-2" colSpan={2}>
                      <form onSubmit={handleUpdate} className="flex gap-2 items-center">
                        <input
                          type="text"
                          required
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                          autoFocus
                        />
                        <label className="flex items-center gap-1.5 text-sm text-gray-700 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={editingRecurring}
                            onChange={(e) => setEditingRecurring(e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          Recurring
                        </label>
                        {editingRecurring && (
                          <input
                            type="number"
                            min="1"
                            value={editingFrequency}
                            onChange={(e) => setEditingFrequency(e.target.value)}
                            placeholder="Months"
                            className="w-20 rounded border border-gray-300 px-2 py-1 text-sm"
                          />
                        )}
                        <Button type="submit" size="sm" isLoading={updateMutation.isPending}>Save</Button>
                        <Button type="button" size="sm" variant="ghost" onClick={cancelEditing}>Cancel</Button>
                      </form>
                    </td>
                    <td></td>
                  </>
                ) : (
                  <>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">{type.name || '-'}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">
                      {type.recurring ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                          Every {type.frequencyMonths} month{type.frequencyMonths !== 1 ? 's' : ''}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-3 text-right space-x-3">
                      <button
                        onClick={() => startEditing(type.id, type.name || '', type.recurring, type.frequencyMonths)}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => { setDeleteId(type.id); setDeleteError(null); }}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Extra Cost Type"
        message="Are you sure you want to delete this extra cost type?"
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
        error={deleteError}
      />
    </div>
  );
}
